import { api } from "./client";

/**
 * Данни за отворените маси в момента
 */
export interface DashboardOpenTables {
  count: number;
  total_amount: string;
  /** Минути, откакто стои най-отдавна отворената маса; null при нула отворени. */
  oldest_minutes?: string | null;
}

/**
 * Данни за затворените сметки за деня
 * cash_amount + card_amount = total_amount (смесените са разпределени)
 */
export interface DashboardClosedToday {
  total_amount: string;
  total_count: number;
  cash_amount: string;
  cash_count: number;
  card_amount: string;
  card_count: number;
  mixed_amount: string;
  mixed_count: number;
  /** Разбивка по метод, КАКТО Е ПЛАТЕНА сметката (смесено = собствен метод) */
  methods: { payment_method_id: number; amount: string; count: string }[];
  /**
   * Разбивката „с номер на бон / без номер" за периода. При `session_access:
   * false` закритите В БРОЙ без бон не влизат в отговора, тоест
   * `unfiscalized_count` не е пълната картина и НЕ бива да се рисува.
   * Опционални, за да не се чупи FE-то срещу по-старо BE.
   */
  fiscalized_count?: number;
  fiscalized_amount?: string;
  unfiscalized_count?: number;
  unfiscalized_amount?: string;
}

/**
 * Оборот за един ден от последните 8 (сумата на закритите сметки по локален
 * ден; без код 2 БЕ-то не брои закритите в брой без бон).
 * total_amount е null ако няма закрити сметки за деня.
 */
export interface DashboardDailyReport {
  date: string;
  total_amount: string | null;
}

/**
 * Агрегати за период „до същия час" — за честно сравнение в средата на деня
 */
export interface DashboardComparisonEntry {
  total_amount: string;
  total_count: number;
  avg_check: string;
}

/** Днес до момента vs вчера / преди 7 дни (все до същия час) */
export interface DashboardComparison {
  today: DashboardComparisonEntry;
  yesterday: DashboardComparisonEntry;
  last_week: DashboardComparisonEntry;
}

export interface DashboardTodayMetrics {
  avg_check: string;
  /** Средно време на маса в минути; null при 0 закрити сметки */
  avg_table_minutes: string | null;
}

export interface DashboardTopItem {
  item_id: number;
  name: string;
  count: string;
  total_sum: string;
}

export interface DashboardVoidsToday {
  count: number;
  total_sum: string;
}

export interface DashboardDiscountsToday {
  bills_count: number;
  total_sum: string;
}

/** Клетка от heatmap-а: конкретна дата + час (редовете са последните дни) */
export interface DashboardHeatmapCell {
  date: string; // YYYY-MM-DD (локална дата)
  weekday: number; // 0=понеделник … 6=неделя
  hour: number;
  count: number;
  amount: string;
}

/** Оборот на станция/обект за днес */
export interface DashboardStationAmount {
  station_id: number;
  name: string;
  amount: string;
}

/** Оборот на един сервитьор за периода (по неговите ЗАКРИТИ сметки). */
export interface DashboardWaiterAmount {
  waiter_id: number;
  name: string;
  amount: string;
  count: number;
  cash_amount: string;
  card_amount: string;
}

/**
 * Вид сигнал в картата „За внимание". `kind` е контрактът, не текстът —
 * преводът се избира по него.
 *
 * Няма „сторно след кухня": етапът на сторниране не се съхранява, така че
 * такъв сигнал би бил догадка, представена като факт.
 *
 * Няма и сигнал за закрита сметка без фискален бон: сигналът е изречение, а
 * този признак се показва БЕЗ ДУМИ — с точка с контур на реда в „История на
 * маси" и с безсловесната разбивка в кутийката „Приключени".
 */
export type DashboardAlertKind = "open_long" | "big_discount";

export interface DashboardAlert {
  kind: DashboardAlertKind;
  bill_id: number;
  table_name: string;
  waiter_name: string;
  amount: string;
  /** Минути при `open_long`, процент при `big_discount`, иначе null. */
  value: string | null;
}

/** Обобщение на предходния равен по дължина период — за Δ чиповете. */
export interface DashboardPeriodTotals {
  total_amount: string;
  total_count: number;
  avg_check: string;
  discounts_total: string;
  voids_total: string;
}

/**
 * Пълен отговор от GET /reports/dashboard
 *
 * ВНИМАНИЕ за имената: `closed_today`, `today_metrics`, `voids_today`,
 * `discounts_today` и `stations_today` са исторически — те следват ИЗБРАНИЯ
 * ПЕРИОД, не непременно днешния ден. Само `open_tables` е състояние „сега".
 */
export interface DashboardResponse {
  open_tables: DashboardOpenTables;
  closed_today: DashboardClosedToday;
  daily_reports: DashboardDailyReport[];
  comparison: DashboardComparison;
  today_metrics: DashboardTodayMetrics;
  top_items: DashboardTopItem[];
  voids_today: DashboardVoidsToday;
  discounts_today: DashboardDiscountsToday;
  heatmap: DashboardHeatmapCell[];
  heatmap_days: number;
  stations_today: DashboardStationAmount[];
  // --- добавени с периодния дашборд (0.0.219); опционални, за да не се чупи
  // FE-то срещу по-старо BE
  waiters?: DashboardWaiterAmount[];
  alerts?: DashboardAlert[];
  items_quantity?: string;
  previous?: DashboardPeriodTotals | null;
  period_start?: string | null;
  period_end?: string | null;
  /**
   * false = сесията е БЕЗ втори код и закритите сметки без номер на бон са
   * скрити от ЦЕЛИЯ отговор. Тогава разбивките, които без тях са 0 по
   * конструкция, НЕ се показват изобщо — нула се чете като „няма такива",
   * което е по-подвеждащо от липсата на показател.
   */
  session_access?: boolean;
}

export interface DashboardFilters {
  language_code?: string;
  /** Локална дата+час; липсват и двете → BE смята днешния ден. */
  start_date?: string;
  end_date?: string;
  /** true → в отговора влиза `previous` за Δ чиповете. */
  compare?: boolean;
  /** false → heatmap-ът не се смята (периодният дашборд не го рисува). */
  include_heatmap?: boolean;
  /**
   * false → заварените агрегати „днес до същия час срещу вчера / преди
   * седмица" (`comparison`) не се смятат — три заявки при всяко зареждане.
   * Периодният дашборд ползва `previous` за избрания период вместо тях.
   */
  include_comparison?: boolean;
}

/**
 * Извлича dashboard данни от API-то
 * GET /reports/dashboard
 */
export const getDashboard = async (
  filters?: DashboardFilters,
): Promise<DashboardResponse> => {
  const params = new URLSearchParams();
  params.append("language_code", filters?.language_code ?? "bg");
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);
  if (filters?.compare) params.append("compare", "true");
  if (filters?.include_heatmap === false) {
    params.append("include_heatmap", "false");
  }
  if (filters?.include_comparison === false) {
    params.append("include_comparison", "false");
  }

  const response = await api.get<DashboardResponse>(
    `/reports/dashboard?${params.toString()}`,
  );
  return response.data;
};
